/**
 * The audience picker a chosen file goes through before `source.upload`:
 * who besides the owner sees it, in the four shapes `Audience` allows
 * (`source.ts`). `Files.tsx` opens this once a file has come off the hidden
 * input, and it is the only place that ever constructs an `Audience`.
 */
import { Button, Callout, Field, Modal, Radio, RadioGroup, Select } from '@wtfalch/design';
import { formatBytes } from '@wtfalch/utils/format';
import { useState } from 'react';

import { roleLabel } from './role.ts';
import type { Audience, Person, Team } from './source.ts';

type Kind = Audience['kind'];

export function UploadDialog({
  file,
  people,
  teams,
  onCancel,
  onConfirm,
}: {
  file: File;
  people: readonly Person[];
  teams: readonly Team[];
  onCancel: () => void;
  /** Resolves once the upload has landed, or throws -- this dialog stays
   *  open and busy until then, and reports a throw rather than closing on
   *  one. */
  onConfirm: (audience: Audience) => Promise<void>;
}) {
  const [kind, setKind] = useState<Kind>('me');
  const [subjects, setSubjects] = useState<string[]>([]);
  const [teamId, setTeamId] = useState(() => teams[0]?.id ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const audience: Audience | null =
    kind === 'me'
      ? { kind: 'me' }
      : kind === 'company'
        ? { kind: 'company' }
        : kind === 'people'
          ? subjects.length > 0
            ? { kind: 'people', subjects }
            : null
          : teamId
            ? { kind: 'team', teamId }
            : null;

  const submit = async () => {
    if (!audience) return;
    setBusy(true);
    setError(null);
    try {
      await onConfirm(audience);
    } catch (err) {
      setBusy(false);
      setError(err instanceof Error ? err.message : 'The upload failed.');
    }
  };

  return (
    <Modal
      title="Who can see this file?"
      subtitle={`${file.name} · ${formatBytes(file.size)}`}
      onClose={onCancel}
      closeDisabled={busy}
      // Who sees a file is a consequential choice, so a stray click on the
      // backdrop must not answer it -- the same reason the design system's
      // own `Dialog` never dismisses on the scrim.
      dismissOnScrim={false}
      footer={
        <>
          <Button kind="ghost" onPress={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button kind="primary" onPress={submit} disabled={!audience} busy={busy}>
            Upload
          </Button>
        </>
      }
    >
      {error && (
        <Callout tone="bad" icon>
          {error}
        </Callout>
      )}
      <RadioGroup label="Visible to" value={kind} onChange={(v) => setKind(v as Kind)}>
        <Radio value="me">Only me</Radio>
        <Radio value="people">People</Radio>
        {teams.length > 0 && <Radio value="team">A team</Radio>}
        <Radio value="company">Everyone in the company</Radio>
      </RadioGroup>
      {kind === 'people' && (
        <Field label="Who">
          <Select
            multiple
            value={subjects}
            onChange={(e) => setSubjects(e.target.value)}
            placeholder="Choose people"
          >
            {people.map((p) => (
              <option key={p.subject} value={p.subject}>
                {roleLabel(p) ? `${p.name} (${roleLabel(p)})` : p.name}
              </option>
            ))}
          </Select>
        </Field>
      )}
      {kind === 'team' && (
        <Field label="Team">
          <Select value={teamId} onChange={(e) => setTeamId(e.target.value)}>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
        </Field>
      )}
    </Modal>
  );
}
